import React, { useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/common/Header";
import SideBar from "../components/common/SideBar";
import IntegrationTable from "../components/systemadministration/Integrations/IntegrationTable";
import IntegrationForm from "../components/systemadministration/Integrations/IntegrationForm";
import IntegrationResources from "../components/systemadministration/Integrations/IntegrationResources";
import AuditTrail from "../components/systemadministration/Integrations/AuditTrail";
import { GetLtiIntegrationDetail } from "../services/LtiIntegrationService";
import SkeletonTicketList from "../loaders/SkeletonTicketList";
import { useDebouncedApiEffect } from "../hooks/useDebouncedApiEffect";
import { usePermissionGuardRedirect } from "../hooks/usePermissionGuard";
import { GlobalIconNames } from "../utils/GlobalIconNames";
import Swal from "sweetalert2";

/**
 * Entry page for LTI integrations under system administration.
*/
function IntegrationsManagement() {
  const history = useHistory();
  const { tab, id, subTab } = useParams();
  const [integrationDetails, setIntegrationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);

  usePermissionGuardRedirect(["integrationsview"]);

  /**
   * Fetches details when user opens an integration.
   */
  async function fetchDetails({ cancelToken }) {
    if (tab !== "open" || !id) {
      setIntegrationDetails(null);
      return;
    }

    try {
      setLoadingDetails(true);
      const response = await GetLtiIntegrationDetail(id, cancelToken);
      const integration = response?.data?.integration || null;
      const responseMessage = String(response?.data?.message || "").toLowerCase();

      if (!integration || responseMessage.includes("integration not found")) {
        setIntegrationDetails(null);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.data?.message || "Integration not found.",
        }).then(() => {
          history.push("/administration/integrations/table");
        });
        return;
      }

      setIntegrationDetails(integration);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      setIntegrationDetails(null);
      const errorMessage = String(error?.response?.data?.message || "").toLowerCase();
      if (
        error?.response?.status === 404 ||
        errorMessage.includes("integration not found")
      ) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Integration not found.",
        }).then(() => {
          history.push("/administration/integrations/table");
        });
      }
    } finally {
      setLoadingDetails(false);
    }
  }

  useDebouncedApiEffect(fetchDetails, [tab, id, detailsRefreshKey]);

  /**
   * Handles successful save flow.
   */
  const handleSaveSuccess = () => {
    if (tab === "create") {
      history.push("/administration/integrations/table");
      return;
    }

    setDetailsRefreshKey(Date.now());
  };

  const detailsTab = subTab || "details";

  return (
    <>
      <Header />
      <div className="content-wrapper d-flex flex-wrap">
        <SideBar />
        <div className="sidebar-right">
          <div className="sub-menu-content-block">
            <div className="sub-menu-right-block">
              <div className="wrap-box-right">
                <div className="my-tickets-heading-blk flex-direction mb-2">
                  <div className="d-flex align-items-center ">
                    <div className="name-w-head d-flex align-items-center ">
                      <h4 className="text-left-align landing-heading heading_color_dynamic">
                        {tab === "table" || tab === "auditTrail" ? (
                          <>
                            LTI <span>Integrations</span>
                          </>
                        ) : tab === "create" ? (
                          <>
                            Create <span>Integration</span>
                          </>
                        ) : (
                          <>
                            Integration: <span>{integrationDetails?.integration_name || ""}</span>
                          </>
                        )}
                      </h4>
                    </div>
                    <div className="text-md-right action2-blk">
                      <div className="ticket-view-btn d-flex align-items-center">
                        <div className="add-new-ticket-bx">
                          <button
                            onClick={() => history.goBack()}
                            title="Back"
                            className="btn btn-white-bordered"
                          >
                            <i className="fal fa-angle-left"></i>Back
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tabs-wrap">
                  {(tab === "table" || tab === "auditTrail") && (
                    <ul className="nav nav-pills mb-4" id="tabs" role="tablist">
                      <li
                        className="nav-item"
                        title="Integrations"
                        onClick={() => history.push("/administration/integrations/table")}
                      >
                        <Link
                          className={`nav-link ${tab === "table" ? "active" : ""}`}
                          id="pills-tab1"
                          to="/administration/integrations/table"
                          role="tab"
                        >
                          <i className="fal fa-plug"></i>Integrations
                        </Link>
                      </li>
                      <li
                        className="nav-item"
                        title="Audit Trails"
                        onClick={() => history.push("/administration/integrations/auditTrail")}
                      >
                        <Link
                          className={`nav-link ${tab === "auditTrail" ? "active" : ""}`}
                          id="pills-tab2"
                          to="/administration/integrations/auditTrail"
                          role="tab"
                        >
                          {GlobalIconNames("audittrail").html}
                        </Link>
                      </li>
                    </ul>
                  )}

                  {tab === "open" && (
                    <ul className="nav nav-pills mb-4" id="tabs" role="tablist">
                      <li className="nav-item">
                        <Link
                          className={`nav-link ${detailsTab === "details" ? "active" : ""}`}
                          id="pills-tab1"
                          to={`/administration/integrations/open/${id}/details`}
                          role="tab"
                        >
                          <i className="fal fa-info-circle"></i>Details
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          className={`nav-link ${detailsTab === "resources" ? "active" : ""}`}
                          id="pills-tab2"
                          to={`/administration/integrations/open/${id}/resources`}
                          role="tab"
                        >
                          <i className="fal fa-books"></i>Resources
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          className={`nav-link ${detailsTab === "auditTrail" ? "active" : ""}`}
                          id="pills-tab3"
                          to={`/administration/integrations/open/${id}/auditTrail`}
                          role="tab"
                        >
                          {GlobalIconNames("audittrail").html}
                        </Link>
                      </li>
                    </ul>
                  )}

                  <div className="tab-content" id="pills-tabContent">
                    <div className="tab-pane fade active show" role="tabpanel">
                      {tab === "table" && <IntegrationTable />}
                      {tab === "auditTrail" && <AuditTrail />}
                      {tab === "create" && <IntegrationForm mode="create" onSaved={handleSaveSuccess} />}
                      {tab === "open" && loadingDetails && detailsTab !== "auditTrail" && (
                        <SkeletonTicketList />
                      )}
                      {tab === "open" && !loadingDetails && detailsTab === "details" && (
                        <IntegrationForm
                          mode="open"
                          integrationId={id}
                          initialData={integrationDetails}
                          onSaved={handleSaveSuccess}
                        />
                      )}
                      {tab === "open" && !loadingDetails && detailsTab === "resources" && (
                        <IntegrationResources integrationId={id} />
                      )}
                      {tab === "open" && detailsTab === "auditTrail" && <AuditTrail />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default IntegrationsManagement;
